import { Divider } from '../../components/Divider';
import { Dropdown } from '../../components/Dropdown';
import { ErrorText } from '../../components/ErrorText';
import { FormCard } from '../../components/FormCard';
import { FormField } from '../../components/FormField';
import { TextInput } from '../../components/FormField/FormField.styles';
import { RadioGroup } from '../../components/RadioGroup';
import { SectionHeader } from '../../components/SectionHeader';
import { SelectablePill } from '../../components/SelectablePill';
import { TextButton } from '../../components/TextButton';
import {
  FormSection,
  GenderError,
  GenderLabel,
  GenderSection,
  HeaderRow,
  Panel,
  PillGroup,
} from './GuestDetailsPanel.styles';
import type { GuestDetailsPanelProps } from './GuestDetailsPanel.types';

const DEFAULT_TITLES = ['Mr', 'Mrs', 'Ms', 'Miss'];
const DEFAULT_GENDERS = [
  { value: 'male', label: 'Male' },
  { value: 'female', label: 'Female' },
];

export function GuestDetailsPanel({
  guestNumber,
  subtitle,
  showHeader = true,
  onRemove,
  existingGuests = [],
  selectedGuestId,
  disabledGuestIds = [],
  onExistingGuestSelect,
  values,
  onChange,
  titleOptions = DEFAULT_TITLES,
  genderOptions = DEFAULT_GENDERS,
  dividerText,
  requiredFields = { title: true, firstName: true, lastName: true },
  showGender = true,
  errors,
  labels,
}: GuestDetailsPanelProps) {
  const resolvedSubtitle = subtitle ?? labels?.subtitle ?? "Tell us who's coming.";
  const resolvedDividerText = dividerText ?? labels?.dividerText ?? 'or';
  const titleLabel = labels?.titleLabel ?? 'Title';
  const titlePlaceholder = labels?.titlePlaceholder ?? 'Select';
  const firstNameLabel = labels?.firstNameLabel ?? 'First name';
  const lastNameLabel = labels?.lastNameLabel ?? 'Last name';
  const removeLabel = labels?.removeLabel ?? 'Remove';
  const genderLabel = labels?.genderLabel ?? 'Gender';
  const guestTitle = labels?.guestTitleFormatter
    ? labels.guestTitleFormatter(guestNumber)
    : `Guest ${guestNumber}.`;

  const titleDropdownOptions = titleOptions.map((option) =>
    typeof option === 'string' ? { value: option, label: option } : option
  );

  const handleTitleChange = (val: string) => {
    onChange('title', val);
    const gender =
      genderOptions.length === 1 ? genderOptions[0].value : val === 'Mr' ? 'male' : 'female';
    onChange('gender', gender);
  };

  const defaultGender = genderOptions.length === 1 ? genderOptions[0].value : '';

  const handleGuestPillClick = (guestId: string) => {
    if (selectedGuestId === guestId) {
      onExistingGuestSelect(undefined);
      onChange('title', '');
      onChange('firstName', '');
      onChange('lastName', '');
      onChange('gender', defaultGender);
    } else {
      const guest = existingGuests.find((g) => g.id === guestId);
      if (guest) {
        onExistingGuestSelect(guestId);
        onChange('title', guest.title);
        onChange('firstName', guest.firstName);
        onChange('lastName', guest.lastName);
        onChange('gender', guest.gender);
      }
    }
  };

  return (
    <Panel>
      {(showHeader || onRemove) && (
        <HeaderRow>
          {showHeader ? <SectionHeader title={guestTitle} subtitle={resolvedSubtitle} /> : <span />}
          {onRemove && (
            <TextButton size="sm" onClick={onRemove}>
              {removeLabel}
            </TextButton>
          )}
        </HeaderRow>
      )}

      {existingGuests.length > 0 && (
        <>
          <PillGroup>
            {existingGuests.map((guest) => (
              <SelectablePill
                key={guest.id}
                label={`${guest.title} ${guest.name}`}
                selected={selectedGuestId === guest.id}
                disabled={disabledGuestIds.includes(guest.id)}
                onClick={() => handleGuestPillClick(guest.id)}
              />
            ))}
          </PillGroup>
          <Divider text={resolvedDividerText} />
        </>
      )}

      <FormSection>
        <FormCard>
          <FormField label={titleLabel} required={requiredFields.title} error={errors?.title}>
            <Dropdown
              options={titleDropdownOptions}
              value={values.title}
              placeholder={titlePlaceholder}
              onChange={(val) => handleTitleChange(val)}
              aria-label={titleLabel}
              hasError={!!errors?.title}
            />
          </FormField>
          <FormField
            label={firstNameLabel}
            required={requiredFields.firstName}
            error={errors?.firstName}
          >
            <TextInput
              name={guestNumber === 1 ? 'firstName' : `guest-${guestNumber}-firstName`}
              value={values.firstName}
              onChange={(e) => onChange('firstName', e.target.value)}
              autoComplete={guestNumber === 1 ? 'given-name' : 'off'}
              aria-label={firstNameLabel}
              aria-required={requiredFields.firstName || undefined}
              aria-invalid={!!errors?.firstName || undefined}
            />
          </FormField>
          <FormField
            label={lastNameLabel}
            required={requiredFields.lastName}
            error={errors?.lastName}
          >
            <TextInput
              name={guestNumber === 1 ? 'lastName' : `guest-${guestNumber}-lastName`}
              value={values.lastName}
              onChange={(e) => onChange('lastName', e.target.value)}
              autoComplete={guestNumber === 1 ? 'family-name' : 'off'}
              aria-label={lastNameLabel}
              aria-required={requiredFields.lastName || undefined}
              aria-invalid={!!errors?.lastName || undefined}
            />
          </FormField>
        </FormCard>
      </FormSection>

      {showGender && (
        <GenderSection>
          <GenderLabel>{genderLabel}</GenderLabel>
          <RadioGroup
            name={`guest-${guestNumber}-gender`}
            options={genderOptions}
            value={values.gender}
            onChange={(val) => onChange('gender', val)}
            hasError={!!errors?.gender}
          />
          {errors?.gender && (
            <GenderError>
              <ErrorText>{errors.gender}</ErrorText>
            </GenderError>
          )}
        </GenderSection>
      )}
    </Panel>
  );
}
